import React, { useState, useRef, useEffect } from 'react';
import {
  FiArrowRight,
  FiPackage,
  FiDroplet,
  FiLayers,
  FiFeather,
  FiSun,
  FiGlobe,
  FiStar,
  FiChevronRight,
} from 'react-icons/fi';
import './Categories.css';

/* ─── CATEGORY DATA ──────────────────────────────────────── */
const categories = [
  {
    id: 'whole',
    slug: '#whole',
    label: 'Whole Spices',
    tagline: 'In their purest form',
    description:
      'Unground, untouched — seeds, pods, barks, and roots harvested at peak potency. Grind fresh for unmatched flavour.',
    emoji: '🌿',
    scene: ['🌱', '🫚', '🌾'],
    count: 48,
    color: '#1a4f0c',
    lightColor: '#2d7a1c',
    accentBg: 'rgba(26,79,12,0.06)',
    gradFrom: '#1a4f0c',
    gradTo: '#2d5a1b',
    size: 'large',            // bento size hint
    icon: <FiPackage />,
    highlight: 'Best for grinding fresh',
  },
  {
    id: 'ground',
    slug: '#ground',
    label: 'Ground Spices',
    tagline: 'Ready when you are',
    description:
      'Stone-milled in small batches to preserve volatile oils. Consistent, vivid, and always cook-ready.',
    emoji: '🟠',
    scene: ['🟤', '🫙', '🧡'],
    count: 62,
    color: '#b85c00',
    lightColor: '#d4780a',
    accentBg: 'rgba(184,92,0,0.06)',
    gradFrom: '#b85c00',
    gradTo: '#c97d0a',
    size: 'medium',
    icon: <FiDroplet />,
    highlight: 'Most popular category',
  },
  {
    id: 'blends',
    slug: '#blends',
    label: 'Spice Blends',
    tagline: 'Crafted by our masters',
    description:
      'Signature recipes perfected over decades. From smoky barbecue rubs to fragrant biryani masalas.',
    emoji: '🫙',
    scene: ['🌶️', '🧄', '🌿'],
    count: 34,
    color: '#7c1d1d',
    lightColor: '#a83232',
    accentBg: 'rgba(124,29,29,0.06)',
    gradFrom: '#7c1d1d',
    gradTo: '#a83232',
    size: 'medium',
    icon: <FiLayers />,
    highlight: 'Chef\'s choice',
  },
  {
    id: 'herbs',
    slug: '#herbs',
    label: 'Organic Herbs',
    tagline: 'Farm-to-jar freshness',
    description:
      'Certified organic, shade-dried at source. Aromatic bouquets that bring the garden straight to your kitchen.',
    emoji: '🌱',
    scene: ['🍃', '💚', '🌿'],
    count: 29,
    color: '#1a5940',
    lightColor: '#237a56',
    accentBg: 'rgba(26,89,64,0.06)',
    gradFrom: '#1a5940',
    gradTo: '#237a56',
    size: 'small',
    icon: <FiFeather />,
    highlight: 'Certified organic',
  },
  {
    id: 'seeds',
    slug: '#seeds',
    label: 'Seeds & Pods',
    tagline: 'The original superfoods',
    description:
      'Cumin, fennel, fenugreek, star anise — the foundational seeds that underpin the world\'s great cuisines.',
    emoji: '🌻',
    scene: ['⭐', '🌰', '🤍'],
    count: 21,
    color: '#5c4a00',
    lightColor: '#8a7010',
    accentBg: 'rgba(92,74,0,0.06)',
    gradFrom: '#5c4a00',
    gradTo: '#8a7010',
    size: 'small',
    icon: <FiSun />,
    highlight: 'Ancient wisdom',
  },
  {
    id: 'exotic',
    slug: '#exotic',
    label: 'Exotic & Rare',
    tagline: 'The world on your shelf',
    description:
      'Long pepper, grains of paradise, sumac berries, kaffir lime leaves — treasures from spice routes few travel.',
    emoji: '✨',
    scene: ['💛', '🌍', '🌺'],
    count: 17,
    color: '#3d1a6e',
    lightColor: '#6b3aad',
    accentBg: 'rgba(61,26,110,0.06)',
    gradFrom: '#3d1a6e',
    gradTo: '#6b3aad',
    size: 'wide',
    icon: <FiGlobe />,
    highlight: 'Limited edition',
  },
];

/* ─── CATEGORY CARD ──────────────────────────────────────── */
const CategoryCard = ({ category, index }) => {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 10;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -10;
    el.style.setProperty('--rx', `${y}deg`);
    el.style.setProperty('--ry', `${x}deg`);
  };

  const handleMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };

  return (
    <a
      ref={cardRef}
      href={category.slug}
      className={`cat-card cat-card--${category.size}`}
      style={{
        '--index': index,
        '--grad-from': category.gradFrom,
        '--grad-to': category.gradTo,
        '--light': category.lightColor,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      aria-label={`Browse ${category.label} — ${category.count} products`}
    >
      {/* Dark gradient skin */}
      <div className="cat-skin" />

      {/* Noise texture overlay */}
      <div className="cat-noise" />

      {/* Scene emojis — decorative background */}
      <div className="cat-scene" aria-hidden="true">
        {category.scene.map((e, i) => (
          <span key={i} className={`scene-emoji scene-emoji--${i + 1}`}>{e}</span>
        ))}
      </div>

      {/* Count badge */}
      <div className="cat-count">
        <FiStar />
        <span>{category.count} products</span>
      </div>

      {/* Main content */}
      <div className="cat-content">
        <div className="cat-icon-wrap">
          <span className="cat-main-emoji" role="img" aria-hidden="true">{category.emoji}</span>
        </div>

        <div className="cat-text">
          <p className="cat-tagline">{category.tagline}</p>
          <h3 className="cat-label">{category.label}</h3>
          <p className="cat-description">{category.description}</p>

          <div className="cat-highlight">
            <span className="highlight-dot" />
            {category.highlight}
          </div>
        </div>
      </div>

      {/* CTA row */}
      <div className="cat-cta">
        <span className="cta-text">Shop now</span>
        <span className="cta-arrow">
          <FiArrowRight />
        </span>
      </div>

      {/* Hover shimmer line */}
      <div className="cat-shimmer" aria-hidden="true" />
    </a>
  );
};

/* ─── MAIN SECTION ───────────────────────────────────────── */
const Categories = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="categories"
      ref={sectionRef}
      className={`categories-section ${isVisible ? 'in-view' : ''}`}
    >
      {/* Section background */}
      <div className="cat-section-bg" aria-hidden="true">
        <div className="cat-bg-glow cat-bg-glow--1" />
        <div className="cat-bg-glow cat-bg-glow--2" />
        <div className="cat-bg-stripe" />
      </div>

      <div className="cat-container">

        {/* ── Header ── */}
        <div className="cat-header">
          <div className="cat-eyebrow">
            <span className="eyebrow-pip" />
            <span>Browse by Category</span>
            <span className="eyebrow-pip" />
          </div>

          <h2 className="cat-title">
            <span className="cat-title-top">Every Spice</span>
            <span className="cat-title-bottom">
              Has a
              <span className="cat-title-gold"> Story</span>
            </span>
          </h2>

          <p className="cat-subtitle">
            Six carefully curated families. Hundreds of individual stories. One
            obsessive commitment to quality at every step of the journey.
          </p>
        </div>

        {/* ── Bento Grid ── */}
        <div className="cat-grid">
          {categories.map((cat, i) => (
            <CategoryCard key={cat.id} category={cat} index={i} />
          ))}
        </div>

        {/* ── Footer strip ── */}
        <div className="cat-footer">
          <p className="cat-footer-text">
            Can't find what you're looking for?
          </p>
          <a href="#all-spices" className="cat-footer-link">
            <span>Browse all 211 products</span>
            <FiChevronRight />
          </a>
        </div>

      </div>
    </section>
  );
};

export default Categories;